import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Flex, FocusTrap, Popover } from '@nula/core-ui';
import { useEntityYjs } from '@/lib/sync/use-entity-yjs';
import { useEntityById } from '@/lib/sync/use-entity-by-id';
import { useDocumentSurfaces } from '@/lib/sync/use-document-surfaces';
import { surfaceDisplayStatus, type SurfaceEntry, type SurfaceSource } from '@/lib/sync/document-surfaces';
import { surfaceViewId } from '@/lib/sync/collaboration/surface-view-id';
import { useLiveRecording } from '@/lib/recordings/use-live-recording';
import { deleteRecording } from '@/lib/recordings/delete-recording';
import { useUIStore } from '@/stores/use-ui-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { useSessionStateOptional } from '@/session/session-context';
import { useFinishedRecordings } from '@/lib/templates/use-finished-recordings';
import { useDocumentEvents } from '@/lib/calendar/use-document-events';
import type { DocumentSourceFacts, TemplateSummary } from '@/lib/templates/catalog';
import { hasBodyText, hasFragmentText } from '@/lib/templates/has-body-text';
import { dismissProposal, regenerateSurface } from '@/lib/templates/api';
import { regenerateRefusalMessage, reviewFailure } from '@/lib/templates/review-copy';
import { useTemplates } from '@/lib/templates/use-templates-store';
import { reportReviewFailure } from '@/lib/templates/report-review-failure';
import { ApiError } from '@/lib/http/client';
import { useSurfaceMutations } from '../hooks/use-surface-mutations';
import { useTemplateRuns } from '../hooks/use-template-runs';
import { useSelectionPassage } from '../hooks/use-selection-passage';
import { useProposal } from '../hooks/use-proposal';
import {
  isReadableTextSurface, passageSourceOption, RECORDING_SURFACE_TITLE, recordingSourceOptions, recordingTimeLabel,
  sourceOptionsFor, type TemplateSourceOption,
} from '../utils/template-sources';
import { SurfaceOrbButton } from './surface-orb';
import { deriveOrbStatus } from '../utils/derive-orb-status';
import { SurfaceLabel } from './surface-label';
import { SurfacePanel } from './surface-panel';
import type { SurfaceRegenerateOffer } from './surface-detail-face';
import { SurfaceBody } from './surface-body';
import styles from './surface-mount.module.css';

const noop = () => {};

function barStatus(entry: SurfaceEntry): string {
  return `${surfaceDisplayStatus(entry)}:${entry.regenerationFailure ?? ''}`;
}

/** Regenerate starts a run, so it needs a template the viewer can start. */
function regenerateModeFor(
  entry: SurfaceEntry,
  template: TemplateSummary | null,
): SurfaceRegenerateOffer['mode'] | null {
  if (!template) return null;
  const status = surfaceDisplayStatus(entry);
  if (status === 'ready') return 'regenerate';
  if (status === 'proposed') return 'review';
  return null;
}

function reportRegenerateRefusal(error: unknown, useLatestVersion: boolean): void {
  if (error instanceof ApiError && error.status === 400 && useLatestVersion) {
    console.warn('regenerate refused', error);
    return;
  }
  reportReviewFailure(error, 'regenerate');
}

interface SurfaceMountProps {
  entityId: string;
  onActiveViewChange?: (viewId: string) => void;
  /**
   * Whether the viewer can mutate surfaces (create/rename/move/delete).
   * Defaults to true. A viewer who cannot edit still sees and can switch
   * between every surface — reading is not an edit (ui-design.md §9.6).
   */
  canEdit?: boolean;
}

/**
 * The feature's entry point: mounts the orb, its panel, and the active-surface
 * state. The one place in the feature allowed to call `useEntityYjs` — the
 * panel and rows below it receive data as props, keeping them mount-stable
 * across surface switches (see the M4 `useYjsSnapshot`/`observeDeep`
 * performance notes this milestone inherits).
 */
export function SurfaceMount({ entityId, onActiveViewChange, canEdit = true }: SurfaceMountProps) {
  const [open, setOpen] = useState(false);
  const [face, setFace] = useState<'surfaces' | 'new' | 'detail' | 'templates' | 'template'>('surfaces');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [templateSources, setTemplateSources] = useState<readonly SurfaceSource[] | null>(null);
  // Which surface the detail face acts on. Held separately from `face` so
  // returning to the list and reopening the same row does not flash a stale
  // title through the transition.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [focusRowId, setFocusRowId] = useState<string | null>(null);
  const [focusCurrentRow, setFocusCurrentRow] = useState(false);

  const { doc } = useEntityYjs(entityId, 'document');
  const entity = useEntityById(entityId, 'document');
  const entries = useDocumentSurfaces(doc);
  const activeEntry = entries.find((entry) => entry.id === activeId) ?? null;

  const openTemplateFace = useCallback((id: string, sources: readonly SurfaceSource[] | null) => {
    setTemplateId(id);
    setTemplateSources(sources);
    setFace('template');
  }, []);
  const runs = useTemplateRuns({
    entityId,
    entries,
    sourceTitle: entity?.title ?? 'Untitled',
    activeId,
    setActiveId,
    listShowing: !open || face === 'surfaces',
    panelOpen: open,
    onOpenPanel: () => setOpen(true),
    onSelect: (id) => handleSelect(id),
    onReturnToList: () => {
      dropPassage();
      setFace('surfaces');
      setFocusCurrentRow(true);
    },
    onRemoveSurface: (id) => handleDeleteRequest(id),
    onOpenTemplate: openTemplateFace,
  });
  const { activate, claimArrivedSurface, clearRefusal } = runs;
  const selection = useSelectionPassage({ entityId, entries, documentTitle: entity?.title ?? 'Untitled' });
  const { passage } = selection;
  const [faceAfterCapture, setFaceAfterCapture] = useState<string | null>(null);
  const [recapturing, setRecapturing] = useState(false);
  // Only ever called from a person's action, never from an effect on `open` or
  // `face`: a template picked from a selection is captured while the panel is
  // still closed.
  function dropPassage() {
    selection.clear();
    setFaceAfterCapture(null);
  }

  // Restore the surface this tab was left on (session-persisted), once the
  // manifest has loaded. Validated against live entries: a remembered surface
  // can have been deleted since, in which case we fall through to the default
  // rather than render an empty page. Runs once per mount — `restoredRef`
  // stops it fighting the user's own selection on later renders.
  const session = useSessionStateOptional();
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || activeId !== null || entries.length === 0) return;
    restoredRef.current = true;
    // ui-design.md §5.4: a surface generated while the person was away opens on
    // their return, ahead of the remembered one. Not their own activation, and
    // focus stays put (§7).
    const arrived = claimArrivedSurface();
    if (arrived) {
      setActiveId(arrived);
      return;
    }
    const remembered = session?.state?.panels
      ? Object.values(session.state.panels)
          .flatMap((p) => p.tabs)
          .find((t) => t.id === entityId)?.surfaceId
      : undefined;
    if (remembered && entries.some((e) => e.id === remembered)) {
      setActiveId(remembered);
    }
  }, [entityId, entries, activeId, session, claimArrivedSurface]);
  const mutations = useSurfaceMutations(doc);
  const detailEntry = face === 'detail' ? entries.find((e) => e.id === detailId) ?? null : null;
  // Read afresh whenever a face that starts a run opens: a template published
  // or bumped since shows without a reload.
  const onTemplateFaces = open && (face === 'templates' || face === 'template');
  const templates = useTemplates({ refreshWhen: onTemplateFaces || (open && face === 'detail') });
  const listed = (id: string | null | undefined) => templates?.find((t) => t.id === id) ?? null;
  const activeTemplate = listed(activeEntry?.generator);
  const detailTemplate = listed(detailEntry?.generator);
  const regenerateMode = canEdit && detailEntry ? regenerateModeFor(detailEntry, detailTemplate) : null;
  const [regenerateBusy, setRegenerateBusy] = useState<string | null>(null);
  const [regenerateRefusal, setRegenerateRefusal] = useState<{ surfaceId: string; message: string } | null>(null);
  const latestChoiceRef = useRef(new Map<string, boolean>());
  const proposal = useProposal(entityId, activeEntry, canEdit);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const reviewingEntry = entries.find((e) => e.id === reviewingId);
  if (reviewingId !== null && (!reviewingEntry || surfaceDisplayStatus(reviewingEntry) !== 'proposed')) {
    setReviewingId(null);
  }
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [barRefusal, setBarRefusal] = useState<{ surfaceId: string; status: string; message: string } | null>(null);
  const barRefusalEntry = barRefusal ? entries.find((e) => e.id === barRefusal.surfaceId) : undefined;
  if (barRefusal !== null && (!barRefusalEntry || barStatus(barRefusalEntry) !== barRefusal.status)) {
    setBarRefusal(null);
  }
  const refuseOnBar = (entry: SurfaceEntry, message: string) =>
    setBarRefusal({ surfaceId: entry.id, status: barStatus(entry), message });
  const [closedByMe, setClosedByMe] = useState<ReadonlyMap<string, string | null>>(new Map());
  const [partiallyApplied, setPartiallyApplied] = useState<ReadonlySet<string>>(new Set());
  const closedNoLongerWaiting = [...closedByMe.keys()].filter((id) => {
    const entry = entries.find((e) => e.id === id);
    return !entry || surfaceDisplayStatus(entry) !== 'proposed';
  });
  if (closedNoLongerWaiting.length > 0) {
    const next = new Map(closedByMe);
    for (const id of closedNoLongerWaiting) next.delete(id);
    setClosedByMe(next);
  }
  const rememberClosed = (surfaceId: string, proposalId: string | null) => {
    setClosedByMe((current) => new Map(current).set(surfaceId, proposalId));
    proposal.refresh();
  };
  // Read only while a template face or a recording shows: nothing else has a use
  // for the recordings, and the body walk is not free.
  const onRecording = activeEntry?.generator === 'recording';
  const finishedRecordings = useFinishedRecordings(entityId, onTemplateFaces || onRecording);
  const { events: linkedEvents, loading: eventsLoading } = useDocumentEvents(entityId, onTemplateFaces);
  const offeredTemplates = templates ?? [];
  const summarizableRecordingId = activeEntry?.recordingId
    ?? finishedRecordings.find((r) => r.surfaceId === activeEntry?.id)?.id
    ?? null;
  const templateFacts: DocumentSourceFacts = {
    hasBodyText: onTemplateFaces && hasBodyText(doc),
    hasSurfaceText:
      onTemplateFaces && entries.some((e) => isReadableTextSurface(e) && hasFragmentText(doc, e.fragment)),
    finishedRecordingIds: finishedRecordings.map((r) => r.id),
    hasPassage: passage?.capture.status === 'captured',
    linkedEventIds: linkedEvents.map((e) => e.eventId),
  };
  const pickedTemplate = listed(templateId);
  const passageCapture = passage?.capture.status === 'captured' ? passage.capture : null;
  const passageOption = passage && passageCapture
    && pickedTemplate?.sourceKinds.includes(passage.surfaceId === null ? 'document' : 'surface')
    ? passageSourceOption({
      entityId, surfaceId: passage.surfaceId, sourceTitle: passage.sourceTitle, excerpt: passageCapture.excerpt,
    })
    : null;
  const listedSourceOptions = sourceOptionsFor(pickedTemplate?.sourceKinds ?? [], {
    entityId,
    title: entity?.title ?? 'Untitled',
    hasBodyText: templateFacts.hasBodyText,
    entries,
    activeId,
    recordings: finishedRecordings,
    events: linkedEvents,
    formatRecordingTime: recordingTimeLabel,
  });
  const passageRefusal = passage?.capture.status === 'refused' ? passage.capture.message : null;
  const sourceOptions = passageOption ? [passageOption, ...listedSourceOptions] : listedSourceOptions;
  const nulaUserId = useAuthStore((s) => s.nulaUserId);
  const liveRecording = useLiveRecording(entityId);
  const isCapturing = liveRecording?.phase === 'capturing';
  // The surface whose OWN recording is the one currently capturing — never
  // "the first audio surface" (design §9.1/M5 Task 4). A document can hold
  // several recordings, so position no longer identifies which one is live;
  // only the recordingId binding (Task 2) does.
  const liveSurface = isCapturing
    ? entries.find((entry) => entry.recordingId === liveRecording?.recordingId) ?? null
    : null;

  // Writes the manifest's recordingId as soon as possible, so the live-badge
  // lookup above (`liveSurface`) and the panel row can reflect the binding
  // immediately rather than waiting on `useRecordingForSurface`'s
  // surfaceId-based resolution (which only kicks in once recordingId stays
  // null). Not load-bearing for correctness — if this effect never runs (the
  // window closes before it fires), `useRecordingForSurface` still recovers
  // the binding from `recording.surfaceId`, which the server persists
  // independently of this write (M5 bug fix).
  //
  // Binds by the surface id the recording was actually started from
  // (`liveRecording.surfaceId`), never by "the first unclaimed entry" —
  // `entries.find` in manifest order picked up a PREVIOUS surface's stale
  // unclaimed entry whenever an earlier bind had been missed, which is
  // exactly the M5 bug's symptom 2 (a second recording appearing to bind the
  // wrong, already-existing surface).
  useEffect(() => {
    if (!liveRecording?.surfaceId) return;
    const target = entries.find((entry) => entry.id === liveRecording.surfaceId);
    if (!target || target.recordingId !== null) return;
    mutations.bindRecording(target.id, liveRecording.recordingId);
  }, [liveRecording, entries, mutations]);

  // Reports the active view upward for RichTextEditor to publish — this
  // component must NOT call usePublishAwarenessView itself: RichTextEditor is
  // the single publisher per mount (see its own call site), and a second
  // publisher here would race on the same awareness local state.
  const viewId = surfaceViewId(activeEntry?.fragment ?? null);
  useEffect(() => {
    onActiveViewChange?.(viewId);
  }, [onActiveViewChange, viewId]);

  // Published keyed by entityId (not a bare global) so the entity title
  // header — mounted above this component with no surface context of its
  // own — can read the active surface per document. A bare global would
  // make two documents open in split view clobber each other's selection.
  // The title and kind travel with the id: the header names the active surface
  // and decides which properties it can carry, and re-deriving either would
  // cost it a second subscription to a manifest it cannot even import.
  const setActiveSurfaceId = useUIStore((s) => s.setActiveSurfaceId);
  const activeTitle = activeEntry?.title;
  const activeSurfaceKind = activeEntry?.surface;
  const activeRecordingId = activeEntry?.recordingId ?? null;
  useEffect(() => {
    setActiveSurfaceId(
      entityId,
      activeId,
      activeTitle !== undefined && activeSurfaceKind !== undefined
        ? { title: activeTitle, surface: activeSurfaceKind, recordingId: activeRecordingId }
        : undefined,
    );
  }, [
    entityId,
    activeId,
    activeTitle,
    activeSurfaceKind,
    activeRecordingId,
    setActiveSurfaceId,
  ]);

  // Persist the selection onto the tab so restarting the app reopens this
  // page, not the first one. Skipped before the manifest resolves, or the
  // null initial state would wipe the remembered id.
  const setTabSurface = session?.setTabSurface;
  useEffect(() => {
    if (!setTabSurface || !restoredRef.current) return;
    setTabSurface(entityId, activeId);
  }, [entityId, activeId, setTabSurface]);

  // A consumer that cannot own the selection — the title bar's breadcrumb —
  // asks for a surface here rather than writing the mirror, and this is where
  // the request is honoured and cleared. Clearing keeps it a one-shot: without
  // it, re-selecting from the orb would be undone by the standing request.
  const requestedSurface = useUIStore((s) => s.requestedSurfaceByEntity[entityId] ?? null);
  const clearSurfaceRequest = useUIStore((s) => s.clearSurfaceRequest);
  useEffect(() => {
    if (!requestedSurface) return;
    activate(requestedSurface.surfaceId);
    clearSurfaceRequest(entityId);
  }, [requestedSurface, entityId, clearSurfaceRequest, activate]);

  useEffect(() => {
    if (passage === null || passage.capture.status !== 'capturing') return;
    setFaceAfterCapture(passage.templateId);
    if (passage.templateId === null) {
      setOpen(true);
      setFace('templates');
    }
  }, [passage]);

  // The same ordering as Try again: the face waits for the panel to be open,
  // or FocusTrap's activation would take focus off Generate.
  useEffect(() => {
    if (faceAfterCapture === null || passage === null || passage.capture.status === 'capturing') return;
    if (!open) {
      setOpen(true);
      return;
    }
    setFaceAfterCapture(null);
    clearRefusal();
    openTemplateFace(faceAfterCapture, null);
  }, [faceAfterCapture, passage, open, clearRefusal, openTemplateFace]);

  const handleOpenChange = (next: boolean) => {
    if (!next) dropPassage();
    setOpen(next);
    // A surfaceless document has no list to land on — skip straight to the
    // New face (ui-design §4.1). Closing always resets so reopening never
    // strands the user on New unexpectedly. A viewer who cannot edit has no
    // New face to skip to at all (§9.6) — always land on the (possibly
    // body-only) surfaces list.
    setFace(next && canEdit && entries.length === 0 ? 'new' : 'surfaces');
  };

  const handleSelect = (id: string | null) => {
    dropPassage();
    activate(id);
    setOpen(false);
    setFace('surfaces');
  };

  const handleNew = () => {
    dropPassage();
    setFace('new');
  };

  const handleBack = () => {
    dropPassage();
    setFace('surfaces');
    setDetailId(null);
  };

  const handleOpenTemplates = () => {
    setFace('templates');
  };

  const handlePickTemplate = (id: string) => {
    if (passage?.capture.status === 'capturing') {
      setFaceAfterCapture(id);
      return;
    }
    setTemplateId(id);
    setTemplateSources(null);
    runs.clearRefusal();
    setFace('template');
  };

  const handleGenerate = (id: string, sources: TemplateSourceOption[]) => {
    if (!sources.some((o) => o.kind === 'passage')) {
      runs.generate(id, sources);
      return;
    }
    setRecapturing(true);
    void selection.refreshIfStale().then((fresh) => {
      setRecapturing(false);
      if (fresh === null || fresh.capture.status !== 'captured') return;
      const passageOption = passageSourceOption({
        entityId, surfaceId: fresh.surfaceId, sourceTitle: fresh.sourceTitle, excerpt: fresh.capture.excerpt,
      });
      runs.generate(id, sources.map((o) => (o.kind === 'passage' ? passageOption : o)));
    });
  };

  const handleOpenDetail = (id: string) => {
    setDetailId(id);
    setRegenerateRefusal(null);
    setFace('detail');
  };

  const handleCreate = (surface: 'text' | 'canvas') => {
    const id = mutations.create('New surface', surface);
    if (id === null) return;
    // Activate as well as name it: the overlay is gated on the active surface,
    // so setting only `editingId` left the new row renameable in the panel with
    // no editor mounted to type into.
    activate(id);
    setEditingId(id);
    setFace('surfaces');
  };

  const handleRecord = () => {
    const id = mutations.create(RECORDING_SURFACE_TITLE, 'audio', 'recording');
    if (id === null) return;
    activate(id);
    setEditingId(id);
    setFace('surfaces');
  };

  /** Rename is inline on the row, so the face closes and the list returns —
   *  you rename a surface while looking at its siblings, at the row's real
   *  width. The face does not reopen afterwards: the action is complete. */
  const handleRenameStart = (id: string) => {
    setFace('surfaces');
    setDetailId(null);
    setEditingId(id);
  };

  const handleRenameCommit = (title: string) => {
    if (editingId === null) return;
    mutations.rename(editingId, title);
    setEditingId(null);
  };

  const handleRenameCancel = () => {
    setFocusRowId(editingId);
    setEditingId(null);
  };

  useEffect(() => {
    if (focusRowId === null || editingId !== null) return;
    const row = document.querySelector<HTMLElement>(
      `[data-surface-id="${focusRowId}"] [role="menuitemradio"]`,
    );
    row?.focus();
    setFocusRowId(null);
  }, [focusRowId, editingId]);

  // Generate unmounts with the template face, which would leave focus on
  // <body>, outside FocusTrap's Escape handler. Not the Starting row: the
  // placeholder replaces it, and focus would drop to <body> again.
  useEffect(() => {
    if (!focusCurrentRow || face !== 'surfaces') return;
    document.querySelector<HTMLElement>('[role="menu"] [role="menuitemradio"][aria-checked="true"]')?.focus();
    setFocusCurrentRow(false);
  }, [focusCurrentRow, face]);

  const startRegeneration = (
    entry: SurfaceEntry,
    useLatestVersion: boolean,
    onAccepted: () => void,
    onRefused: (message: string) => void,
  ) => {
    if (!entry.generator || regenerateBusy !== null) return;
    latestChoiceRef.current.set(entry.id, useLatestVersion);
    setRegenerateBusy(entry.id);
    setRegenerateRefusal(null);
    setBarRefusal(null);
    void regenerateSurface(
      entry.generator,
      { entityId, surfaceId: entry.id },
      useLatestVersion ? { useLatestVersion } : undefined,
    ).then(
      () => {
        setRegenerateBusy(null);
        onAccepted();
      },
      (error: unknown) => {
        setRegenerateBusy(null);
        reportRegenerateRefusal(error, useLatestVersion);
        const message = regenerateRefusalMessage(error, { useLatestVersion });
        if (message === null) {
          onAccepted();
          return;
        }
        onRefused(message);
      },
    );
  };

  const leavePanelFor = (id: string) => {
    activate(id);
    setOpen(false);
    setFace('surfaces');
    setDetailId(null);
  };

  const readyProposal = proposal.state.kind === 'ready' ? proposal.state.proposal : null;
  const proposalReadFailed = proposal.state.kind === 'error';
  const reviewAgain = (id: string) => {
    setReviewingId(id);
    proposal.refresh();
  };

  const detailRegenerate: SurfaceRegenerateOffer | undefined = detailEntry && detailTemplate && regenerateMode ? {
    mode: regenerateMode,
    latest: regenerateMode === 'regenerate' && detailEntry.templateVersion !== undefined
      && detailTemplate.currentVersion > detailEntry.templateVersion
      ? { templateName: detailTemplate.name }
      : null,
    busy: regenerateBusy === detailEntry.id,
    refusal: regenerateRefusal?.surfaceId === detailEntry.id ? regenerateRefusal.message : null,
    onRegenerate: (useLatestVersion) =>
      startRegeneration(
        detailEntry,
        useLatestVersion,
        () => leavePanelFor(detailEntry.id),
        (message) => setRegenerateRefusal({ surfaceId: detailEntry.id, message }),
      ),
    onReview: () => {
      if (proposalReadFailed && activeEntry?.id === detailEntry.id) reviewAgain(detailEntry.id);
      else setReviewingId(detailEntry.id);
      leavePanelFor(detailEntry.id);
    },
  } : undefined;

  const closedHere = activeEntry ? closedByMe.get(activeEntry.id) : undefined;
  const bannerHidden = closedHere !== undefined
    && (proposal.state.kind === 'loading' || readyProposal?.proposalId === closedHere);
  const dismissOffered = !readyProposal || (
    !partiallyApplied.has(readyProposal.proposalId)
    && !readyProposal.sections.some((section) => section.outcome === 'applied')
  );

  const handleDismiss = (entry: SurfaceEntry) => {
    if (dismissing !== null) return;
    const surfaceId = entry.id;
    setDismissing(surfaceId);
    setBarRefusal(null);
    const proposalId = readyProposal?.proposalId ?? null;
    void dismissProposal(entityId, surfaceId, proposalId ?? undefined).then(
      () => {
        setDismissing(null);
        rememberClosed(surfaceId, proposalId);
      },
      (error: unknown) => {
        setDismissing(null);
        reportReviewFailure(error, 'dismiss');
        const failure = reviewFailure(error);
        if (failure === 'replaced' || failure === 'closed' || failure === 'removed') proposal.refresh();
        if (typeof failure === 'object') refuseOnBar(entry, failure.message);
      },
    );
  };

  /** The detail face arms this itself (two clicks, like the recents menu), so
   *  by the time it fires the user has already confirmed — except Stop and
   *  remove on a generating surface, which fires on the first click.
   *
   *  A surface holding a recording takes that recording with it — otherwise
   *  the audio survives in S3 with the only thing that referenced it gone.
   *  The manifest entry is removed regardless: a failed server delete leaves
   *  the row (and its audio) intact for the retention sweep to retry, which is
   *  the safe direction, and blocking the UI on it would strand the user on a
   *  surface they asked to remove. */
  const handleDeleteRequest = (id: string) => {
    const entry = entries.find((e) => e.id === id);
    if (!entry) return;
    setFace('surfaces');
    setDetailId(null);
    if (entry.recordingId) {
      void deleteRecording(entry.recordingId).catch((error: unknown) => {
        Sentry.captureException(error);
        console.error('surface_delete_recording_failed', error);
      });
    }
    mutations.remove(id);
    runs.retireRunsFor(id);
    if (activeId === id) setActiveId(null);
  };

  return (
    <>
      {activeEntry && (
        <Flex className={styles.body} data-surface-overlay-active="true">
          <SurfaceBody
            entityId={entityId}
            entry={activeEntry}
            canEdit={canEdit}
            sourceTitles={runs.sourceTitlesFor(activeEntry)}
            viewerIsRequester={!!nulaUserId && nulaUserId === activeEntry.requestedBy}
            matchedRunStage={runs.matchedRunStage}
            onTryAgain={activeTemplate ? () => runs.tryAgain(activeEntry.id, activeTemplate.id) : undefined}
            onRemove={() => handleDeleteRequest(activeEntry.id)}
            proposal={proposal.state}
            reviewing={reviewingId === activeEntry.id}
            templateName={activeTemplate?.name ?? null}
            onReview={readyProposal
              ? () => setReviewingId(activeEntry.id)
              : proposalReadFailed ? () => reviewAgain(activeEntry.id) : undefined}
            onDismiss={dismissOffered ? () => handleDismiss(activeEntry) : undefined}
            onRegenerate={activeTemplate && canEdit
              ? () => startRegeneration(
                activeEntry,
                latestChoiceRef.current.get(activeEntry.id) ?? false,
                noop,
                (message) => refuseOnBar(activeEntry, message),
              )
              : undefined}
            onReviewClosed={(settled) => {
              setReviewingId(null);
              if (settled) rememberClosed(activeEntry.id, readyProposal?.proposalId ?? null);
            }}
            onProposalReplaced={proposal.refresh}
            onPartiallyApplied={(id) => setPartiallyApplied((current) => new Set(current).add(id))}
            bannerHidden={bannerHidden}
            bannerBusy={dismissing === activeEntry.id || regenerateBusy === activeEntry.id}
            bannerMessage={barRefusal?.surfaceId === activeEntry.id ? barRefusal.message : null}
            summarize={onRecording && canEdit
              ? runs.summaryOffer(
                activeEntry,
                summarizableRecordingId,
                recordingSourceOptions(finishedRecordings, entries, recordingTimeLabel),
              )
              : undefined}
          />
        </Flex>
      )}
      <Flex className={styles.wrapper}>
        <Popover
          placement="top-start"
          trigger="click"
          open={open}
          onOpenChange={handleOpenChange}
          arrow={false}
          padded={false}
          radius="2xl"
          content={(
            <FocusTrap
              active={open}
              modal={false}
              onEscape={() => handleOpenChange(false)}
            >
              <SurfacePanel
                bodyTitle={entity?.title ?? 'Untitled'}
                entries={entries}
                activeId={activeId}
                onSelect={handleSelect}
                onNew={handleNew}
                face={face}
                onCreate={handleCreate}
                onBack={handleBack}
                editingId={editingId}
                detailId={detailId}
                onOpenDetail={handleOpenDetail}
                onRenameStart={handleRenameStart}
                onRenameCommit={handleRenameCommit}
                onRenameCancel={handleRenameCancel}
                onMove={mutations.move}
                onDelete={handleDeleteRequest}
                canEdit={canEdit}
                onRecord={handleRecord}
                canRecord
                liveSurfaceId={liveSurface?.id ?? null}
                templates={offeredTemplates}
                templateFacts={templateFacts}
                onOpenTemplates={handleOpenTemplates}
                templateId={templateId}
                onPickTemplate={handlePickTemplate}
                sources={sourceOptions}
                initialSources={templateSources}
                templateRefusal={passageRefusal ?? runs.templateRefusal}
                templateBusy={runs.templateBusy}
                templatePending={
                  recapturing || (eventsLoading && !!pickedTemplate?.sourceKinds.includes('calendar_event'))
                }
                onGenerate={handleGenerate}
                pendingRows={runs.pendingRows}
                onRemovePending={runs.removeRun}
                detailRegenerate={detailRegenerate}
              />
            </FocusTrap>
          )}
        >
          <SurfaceOrbButton
            count={entries.length}
            displayCount={entries.length + 1}
            open={open}
            status={deriveOrbStatus(entries, isCapturing)}
          />
        </Popover>
        {!open && entries.length > 0 && (
          <SurfaceLabel
            bodyTitle={entity?.title ?? 'Untitled'}
            entry={activeEntry}
            onClick={() => setOpen(true)}
          />
        )}
      </Flex>
    </>
  );
}
